import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { usePermissionCheck } from '@/hooks/usePermissionCheck';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Car,
  Users,
  Package,
  DollarSign,
  Clock,
  Activity,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import DataTablePagination from '@/components/ui/data-table-pagination';
import { useVehicleCategories, useDeleteVehicleCategory, useToggleVehicleCategoryStatus } from '@/api-services/hooks/useVehicleCategory';
import { VehicleCategory, StatusOption } from '@/api-services/hooks/vehicleCategoryTypes';
import DeleteVehicleCategoryConfirmation from '@/components/vehicle-categories/DeleteVehicleCategoryConfirmation';
import { formatGlobalCurrency } from '@/api-services/hooks/useSettings';

const VehicleCategoriesPage = () => {
  const can = usePermissionCheck();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusOption>('all');

  // Ref to maintain focus on search input
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Pagination state (0-indexed for API)
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Delete functionality state
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [vehicleCategoryToDelete, setVehicleCategoryToDelete] = useState<{ id: number; details: string } | null>(null);

  // Handle search input change
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Determine if we need client-side filtering
  const hasClientSideFilters = statusFilter !== 'all';
  
  // When client-side filters are active, fetch a large page size to have enough data to filter
  // Otherwise, use normal pagination
  const apiPageSize = hasClientSideFilters ? 1000 : itemsPerPage;
  const apiPage = hasClientSideFilters ? 0 : currentPage; // Always fetch from page 0 when filtering client-side

  // Fetch vehicle categories from API (only search filter goes to API, status filter is client-side)
  // Only send search to API if search term is more than 3 characters
  const shouldSearch = debouncedSearchTerm.trim().length > 2;
  const { data, isLoading: loading, error, refetch } = useVehicleCategories(
    {
      page: apiPage,
      size: apiPageSize,
      ...(shouldSearch && { search: debouncedSearchTerm.trim() }),
    },
    {
      enabled: true, // Always enabled, but search param only included if > 3 chars
      onError: (err) => {
        toast.error(`Failed to load vehicle categories: ${err.message}`);
      },
    }
  );

  // Auto-focus search input on page load (after loading completes)
  useEffect(() => {
    if (!loading && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [loading]);

  // Reset to first page when search filter changes (client-side filters don't reset page)
  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearchTerm]);

  // Delete vehicle category hook
  const { mutate: deleteVehicleCategory, isPending: isDeleting } = useDeleteVehicleCategory({
    onSuccess: async (data, categoryId) => {
      toast.success(data.message);
      // Invalidate all vehicle categories queries to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: ['vehicle-categories'] });
    },
    onError: (error, categoryId) => {
      toast.error(`Failed to delete vehicle category: ${error.message}`);
    },
  });

  // Toggle vehicle category status hook
  const { mutate: toggleStatus, isPending: isToggling } = useToggleVehicleCategoryStatus({
    onSuccess: async (data, categoryId) => {
      toast.success('Vehicle category status updated successfully!');
      // Update only the changed row in the cache instead of refetching entire table
      queryClient.setQueriesData(
        { queryKey: ['vehicle-categories'] },
        (oldData: any) => {
          if (!oldData?.content) return oldData;

          const updatedContent = oldData.content.map((category: any) =>
            category.vehicleCategoryId === categoryId
              ? { ...category, isActive: !category.isActive }
              : category
          );

          return {
            ...oldData,
            content: updatedContent,
          };
        }
      );
    },
    onError: (error, categoryId) => {
      toast.error(`Failed to update vehicle category status: ${error.message}`);
    },
  });

  const getStatusBadge = useCallback((isActive: boolean) => {
    return isActive
      ? { variant: 'default', className: 'bg-success text-success-foreground', label: 'Active' }
      : { variant: 'secondary', className: 'bg-muted text-muted-foreground', label: 'Inactive' };
  }, []);

  const handleVehicleCategoryAction = (action: string, categoryId: number, categoryDetails: string) => {
    if (action === 'Edit') {
      navigate(`/admin/vehicle-categories/edit/${categoryId}`);
    } else if (action === 'Toggle Status') {
      toggleStatus(categoryId);
    }
  };

  // Delete handler functions
  const handleDeleteClick = (categoryId: number, categoryDetails: string) => {
    setVehicleCategoryToDelete({ id: categoryId, details: categoryDetails });
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirm = (categoryId: number) => {
    deleteVehicleCategory(categoryId);
    setDeleteConfirmationOpen(false);
    setVehicleCategoryToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmationOpen(false);
    setVehicleCategoryToDelete(null);
  };

  // Filter vehicle categories by status on the client side
  const vehicleCategories = useMemo(() => {
    const categoryData = data as any;
    if (!categoryData || !categoryData.content) return [];

    let filtered = categoryData.content;

    // Filter by status (active/inactive)
    if (statusFilter !== 'all') {
      filtered = filtered.filter((category: VehicleCategory) => {
        if (statusFilter === 'active') {
          return category.isActive === true;
        } else if (statusFilter === 'inactive') {
          return category.isActive === false;
        }
        return true;
      });
    }

    return filtered;
  }, [data, statusFilter, hasClientSideFilters]);

  // Pagination calculations - use filtered count for client-side filters
  const totalFilteredItems = vehicleCategories.length;
  const categoryData = data as any;
  const totalItems = categoryData?.page?.totalElements ?? 0;
  
  // Calculate total pages based on filtered items
  const totalPages = useMemo(() => {
    if (hasClientSideFilters) {
      // Client-side filtering: paginate filtered results
      return totalFilteredItems > 0 ? Math.ceil(totalFilteredItems / itemsPerPage) : 0;
    }
    // Server-side pagination: use API response
    const pages = categoryData?.page?.totalPages ?? 0;
    // If totalPages is 0 but we have totalElements, calculate it
    if (pages === 0 && categoryData?.page?.totalElements && categoryData?.page?.totalElements > 0) {
      return Math.ceil(categoryData?.page?.totalElements / itemsPerPage);
    }
    // If totalPages is still 0 but we have content, calculate from content length
    if (pages === 0 && categoryData?.content && categoryData.content.length > 0) {
      return Math.ceil(categoryData.content.length / itemsPerPage);
    }
    return pages;
  }, [totalFilteredItems, itemsPerPage, hasClientSideFilters, categoryData]);

  // Paginated vehicle categories for current page (when using client-side filtering)
  const paginatedVehicleCategories = useMemo(() => {
    if (!hasClientSideFilters) {
      // No client-side filtering, use API pagination directly
      return categoryData?.content ?? [];
    }
    // Client-side pagination for filtered results
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return vehicleCategories.slice(startIndex, endIndex);
  }, [vehicleCategories, currentPage, itemsPerPage, hasClientSideFilters, categoryData]);

  const handlePageChange = (page: number) => {
    // Convert from 1-indexed (UI) to 0-indexed (API)
    setCurrentPage(page - 1);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(0); // Reset to first page when items per page changes
  };

  // Check if current page is empty after data loads and navigate to previous page if needed
  useEffect(() => {
    if (!loading && categoryData && paginatedVehicleCategories.length === 0 && currentPage > 0 && totalPages > 0) {
      // Current page is empty, navigate to the last valid page
      const lastPage = Math.max(0, totalPages - 1);
      setCurrentPage(lastPage);
    }
  }, [loading, paginatedVehicleCategories.length, currentPage, totalPages, categoryData]);

  // Format vehicle category details for display - wrapped in useCallback to prevent infinite re-renders
  const getVehicleCategoryDetails = useCallback((category: VehicleCategory) => {
    return `${category.vehicleCategoryName || 'Unknown Category'}`;
  }, []);

  const getCurrencyDisplay = useCallback((amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading vehicle categories...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <TrendingDown className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Error Loading Vehicle Categories</h3>
            <p className="text-muted-foreground mb-4">{error.message}</p>
            <Button onClick={() => refetch()}>Try Again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Vehicle Categories</h1>
          <p className="text-muted-foreground mt-1">Manage vehicle types, pricing, and capacity settings</p>
        </div>
        {can('Create Vehicle Categories') && (
          <Button
            className="bg-gradient-primary hover:opacity-90"
            onClick={() => navigate('/admin/vehicle-categories/add')}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Vehicle Category
          </Button>
        )}
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                type="search"
                placeholder="Search vehicle categories by name... (min 3 characters)"
                className="pl-10"
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                autoComplete="off"
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-40">
                  <Filter className="w-4 h-4 mr-2" />
                  {statusFilter === 'all' ? 'All Status' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setStatusFilter('all')}>All Status</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('active')}>Active</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('inactive')}>Inactive</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>

      {/* Vehicle Categories Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Car className="w-5 h-5" />
            Vehicle Categories (
            {loading 
              ? "..." 
              : hasClientSideFilters 
                ? `${totalFilteredItems} filtered` 
                : totalItems > 0 ? totalItems : (categoryData?.content?.length ?? 0)}
            )
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full caption-bottom text-sm border-collapse min-w-[640px]">
              <thead className="[&_tr]:border-b">
                <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                  <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground">Category Name</th>
                  <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground">Capacity</th>
                  <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground">Base Fare</th>
                  <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground hidden md:table-cell">Rate/KM</th>
                  <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground hidden md:table-cell">Rate/Min</th>
                  {/* <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground hidden lg:table-cell">Min Fare</th> */}
                  <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground hidden lg:table-cell">Night Charge</th>
                  <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground hidden lg:table-cell">Cancellation Fee</th>
                  <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground hidden xl:table-cell">Toll Fee</th>
                  <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground">Status</th>
                  <th className="h-10 px-2 text-right align-middle font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="[&_tr:last-child]:border-0">
                {paginatedVehicleCategories.length === 0 ? (
                  <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                    <td colSpan={11} className="p-2 align-middle text-center py-8 text-muted-foreground">
                      {vehicleCategories.length === 0
                        ? "No vehicle categories found"
                        : "No vehicle categories match the current filters"}
                    </td>
                  </tr>
                ) : (
                  paginatedVehicleCategories.map((category) => {
                    const statusBadge = getStatusBadge(category.isActive);
                    const categoryDetails = getVehicleCategoryDetails(category);

                    return (
                      <tr key={category.vehicleCategoryId} className="border-b transition-colors hover:bg-muted/50">
                        <td className="p-2 align-middle">
                          <div className="flex items-center gap-2">
                            <Avatar className="h-8 w-8">
                              {category.vehicleIcon && (
                                <AvatarImage src={category.vehicleIcon} alt={category.vehicleCategoryName} className="object-contain p-0.5" />
                              )}
                              <AvatarFallback className="bg-blue-100 text-blue-800 text-xs">
                                <Car className="h-4 w-4" />
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="font-medium text-sm">{category.vehicleCategoryName || 'Unknown'}</div>
                              <div className="text-xs text-muted-foreground">ID: {category.vehicleCategoryId}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-2 align-middle">
                          <div className="flex items-center gap-1">
                            <Users className="w-3 h-3 text-blue-500" />
                            <span className="text-sm">{category.maxPassengerCount}</span>
                          </div>
                        </td>
                        <td className="p-2 align-middle">
                          <span className="text-sm font-semibold text-green-600">{formatGlobalCurrency(category.baseFare)}</span>
                        </td>
                        <td className="p-2 align-middle hidden md:table-cell">
                          <span className="text-sm text-blue-600">{formatGlobalCurrency(category.ratePerKm)}</span>
                        </td>
                        <td className="p-2 align-middle hidden md:table-cell">
                          <span className="text-sm text-purple-600">{formatGlobalCurrency(category.ratePerMinute)}</span>
                        </td>                        
                        {/*
                       <td className="p-2 align-middle hidden lg:table-cell">
                          <span className="text-sm text-orange-600">{category.minimumFare ? formatGlobalCurrency(category.minimumFare) : '-'}</span>
                        </td>
                        */}
                        <td className="p-2 align-middle hidden lg:table-cell">
                          <span className="text-sm text-amber-600">{category.nightCharges ? formatGlobalCurrency(category.nightCharges) : '-'}</span>
                        </td>
                        <td className="p-2 align-middle hidden lg:table-cell">
                          <span className="text-sm text-red-600">{formatGlobalCurrency(category.cancellationFee)}</span>
                        </td>
                        <td className="p-2 align-middle hidden xl:table-cell">
                          <span className="text-sm">{(category.tollFeeApplicable ?? category.isTollFeeApplicable) ? 'Yes' : 'No'}</span>
                        </td>
                        <td className="p-2 align-middle">
                          <Badge className={statusBadge.className} style={{ fontSize: '0.7rem' }}>
                            {category.isActive ? (
                              <>
                                <ToggleRight className="w-3 h-3 mr-1" />
                                {statusBadge.label}
                              </>
                            ) : (
                              <>
                                <ToggleLeft className="w-3 h-3 mr-1" />
                                {statusBadge.label}
                              </>
                            )}
                          </Badge>
                        </td>
                        <td className="p-2 align-middle text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {can('Edit Vehicle Categories') && (
                                <DropdownMenuItem onSelect={() => handleVehicleCategoryAction('Edit', category.vehicleCategoryId || 0, categoryDetails)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit Category
                                </DropdownMenuItem>
                              )}
                              {can('Edit Vehicle Categories') && (
                                <DropdownMenuItem
                                  onSelect={() => handleVehicleCategoryAction('Toggle Status', category.vehicleCategoryId || 0, categoryDetails)}
                                  disabled={isToggling}
                                >
                                  {category.isActive ? (
                                    <>
                                      <ToggleLeft className="w-4 h-4 mr-2" />
                                      Deactivate
                                    </>
                                  ) : (
                                    <>
                                      <ToggleRight className="w-4 h-4 mr-2" />
                                      Activate
                                    </>
                                  )}
                                </DropdownMenuItem>
                              )}
                              {can('Delete Vehicle Categories') && (
                                <DropdownMenuItem
                                  onSelect={() => handleDeleteClick(category.vehicleCategoryId || 0, categoryDetails)}
                                  className="text-destructive"
                                  disabled={isDeleting}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete Category
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination - Always show if totalPages > 0, even if current page is empty */}
          {totalPages > 0 && (
            <div className="p-6 pt-4">
              <DataTablePagination
                currentPage={currentPage + 1} // Convert from 0-indexed to 1-indexed for UI
                totalPages={totalPages}
                totalItems={hasClientSideFilters ? totalFilteredItems : totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Vehicle Category Confirmation Modal */}
      <DeleteVehicleCategoryConfirmation
        open={deleteConfirmationOpen}
        onOpenChange={handleDeleteCancel}
        vehicleCategoryDetails={vehicleCategoryToDelete?.details || ''}
        vehicleCategoryId={vehicleCategoryToDelete?.id || 0}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default VehicleCategoriesPage;